import React, { useState, useRef, useCallback } from 'react';

interface EnvironmentBannerProps {
  environment: 'test' | 'local' | null;
  productionReadonlyMode?: boolean;
}

const EnvironmentBanner: React.FC<EnvironmentBannerProps> = ({ environment, productionReadonlyMode }) => {
  const [offsetX, setOffsetX] = useState(0);
  const dragRef = useRef<{ startX: number; startOffset: number } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragRef.current = { startX: e.clientX, startOffset: offsetX };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [offsetX]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const delta = e.clientX - dragRef.current.startX;
    setOffsetX(dragRef.current.startOffset + delta);
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);
  if (!environment && !productionReadonlyMode) {
    return null;
  }

  const config = {
    test: {
      label: 'Test Environment',
      gradient: 'bg-gradient-to-r from-red-500 to-red-700',
      icon: 'warning'
    },
    local: {
      label: 'Local Environment',
      gradient: 'bg-gradient-to-r from-blue-500 to-blue-700',
      icon: 'warning'
    },
  };

  const envConfig = environment ? config[environment] : null;

  // Warning triangle icon
  const WarningIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
    </svg>
  );

  // Database with lock icon for readonly mode
  const DatabaseLockIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"></path>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11v2m-1.5 2h3"></path>
    </svg>
  );

  return (
    <div
      className="fixed top-0 left-1/2 z-[9999] flex items-start gap-[5px] cursor-grab active:cursor-grabbing select-none"
      style={{ transform: `translateX(calc(-50% + ${offsetX}px))` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Environment banner - centered */}
      {envConfig && (
        <button
          className={`${envConfig.gradient} text-white px-4 py-2 text-sm font-medium focus:outline-none hover:opacity-90 rounded-b-lg flex items-center space-x-2 shadow-lg`}
        >
          <WarningIcon />
          <span>{envConfig.label}</span>
        </button>
      )}

      {/* Production Readonly banner - 5px to the right */}
      {productionReadonlyMode && (
        <button
          className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-4 py-2 text-sm font-medium focus:outline-none hover:opacity-90 rounded-b-lg flex items-center space-x-2 shadow-lg"
        >
          <DatabaseLockIcon />
          <span>Production Readonly - Writes Blocked</span>
        </button>
      )}
    </div>
  );
};

export default EnvironmentBanner;
